import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Input, Label, NativeSelect, Tab, TabList } from "@realroof/ui";

import { api, canManage } from "../api";
import { useSession } from "../session";
import { Table, td, th } from "../table";
import { CityChooser, type Place } from "./Locations";

export type HomeKind = "slide" | "try" | "pick" | "project" | "locality";

export interface HomeItem {
  id: string;
  kind: HomeKind;
  city_id: string | null;
  city: string | null;
  position: number;
  active: boolean;
  starts_on: string | null;
  ends_on: string | null;
  headline: string | null;
  text: string | null;
  scene: string | null;
  image_key: string | null;
  image: string | null;
  alt: string | null;
  link: string | null;
  label: string | null;
  public_id: string | null;
  title: string | null;
  listing_status: string | null;
  location_id: string | null;
  location: string | null;
  showing: boolean;
}

const TABS: [HomeKind, string, string, string][] = [
  ["slide", "Hero slides", "a hero slide", "The moving hero at the top. Until you add slides, our three drawn scenes show. A photo replaces the drawing: use one you own (your own photo, or one with a written copyright assignment), wide, about 2400 × 1000."],
  ["try", "Example searches", "an example search", "The chips under the search box, e.g. “Hall for 300 guests” → /search?module=hall&guests=300. Until you add some, three general ones show."],
  ["pick", "Picked for you", "a picked ad", "Ads shown first under “Picked for you”. The row is filled with the newest live ads with photos (premium first)."],
  ["project", "New projects", "a project", "Projects shown first under “New projects from verified builders”; the rest are the newest live projects."],
  ["locality", "Localities", "a locality", "Localities shown first under “Localities people are searching”; the rest are the ones searched most in the last 30 days."],
];
const SCENES: [string, string][] = [
  ["house", "House (full-width scene)"],
  ["skyline", "City skyline with a function hall"],
  ["tiles", "Picture tiles: homes, halls, catering"],
];

/** What the home page shows (console "Home page"): per city, or everywhere when no city is chosen. */
export function HomePageAdmin() {
  const { me } = useSession();
  const [kind, setKind] = useState<HomeKind>("slide");
  const [city, setCity] = useState<Place | null>(null);
  const [rows, setRows] = useState<HomeItem[] | null>(null);
  const [editing, setEditing] = useState<HomeItem | "new" | null>(null);
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const manage = canManage(me?.staff_role);
  const tab = TABS.find((t) => t[0] === kind)!;

  const load = () =>
    api<HomeItem[]>(`/admin/home?kind=${kind}&city=${city ? city.id : "none"}`)
      .then(setRows)
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    setRows(null);
    setEditing(null);
    setMessage(null);
    load();
  }, [kind, city]); // eslint-disable-line react-hooks/exhaustive-deps

  async function remove(item: HomeItem) {
    setMessage(null);
    try {
      await api(`/admin/home/${item.id}`, { method: "DELETE" });
      setMessage({ kind: "success", text: "Removed from the home page." });
      await load();
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Home page</h1>
      <TabList aria-label="What to change">
        {TABS.map(([k, label]) => (
          <Tab key={k} selected={kind === k} className="flex-none" onClick={() => setKind(k)}>
            {label}
          </Tab>
        ))}
      </TabList>
      <p className="text-sm text-muted-foreground">{tab[3]}</p>
      <CityChooser label="City (leave empty for everywhere)" value={city} onChange={setCity} />
      <p className="text-sm text-muted-foreground">
        {city
          ? `Showing ${tab[1].toLowerCase()} for ${city.name}. A city with none of its own uses its hub city's, then the ones for everywhere.`
          : `Showing ${tab[1].toLowerCase()} for everywhere: used in any city that has none of its own.`}
      </p>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
      {manage && !editing && (
        <div>
          <Button variant="primary" onClick={() => setEditing("new")}>
            + Add {tab[2]}
          </Button>
        </div>
      )}
      {editing && (
        <ItemForm
          kind={kind}
          city={city}
          item={editing === "new" ? null : editing}
          next={(rows?.length ?? 0) + 1}
          onDone={(text) => {
            setEditing(null);
            setMessage({ kind: "success", text });
            load();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {rows === null && <p className="text-muted-foreground">Loading…</p>}
      {rows?.length === 0 && <p className="text-muted-foreground">Nothing added here yet: the home page uses the automatic choice.</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Order</th>
              <th className={th}>{tab[1]}</th>
              <th className={th}>Dates</th>
              <th className={th}>Status</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className={td}>{r.position}</td>
                <td className={td}>
                  <What item={r} />
                </td>
                <td className={`${td} whitespace-nowrap`}>{r.starts_on || r.ends_on ? `${r.starts_on ?? "now"} → ${r.ends_on ?? "no end"}` : "Always"}</td>
                <td className={td}>
                  {r.showing ? (
                    <Badge tone="success">Showing</Badge>
                  ) : (
                    <Badge tone="neutral">{!r.active ? "Off" : r.listing_status && r.listing_status !== "live" ? "Ad not live" : "Not in its dates"}</Badge>
                  )}
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {manage && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => setEditing(r)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)}>
                        Remove
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}

function What({ item: r }: { item: HomeItem }) {
  if (r.kind === "slide")
    return (
      <div className="flex items-center gap-2">
        {r.image && <img src={r.image} alt="" className="h-12 w-24 rounded object-cover" />}
        <div>
          <b>{r.headline}</b>
          <div className="text-xs text-muted-foreground">
            {r.text} · {r.image ? "photo" : SCENES.find((s) => s[0] === r.scene)?.[1]}
          </div>
        </div>
      </div>
    );
  if (r.kind === "try")
    return (
      <div>
        <b>{r.label}</b>
        <div className="font-mono text-xs text-muted-foreground">{r.link}</div>
      </div>
    );
  if (r.kind === "locality") return <b>{r.location}</b>;
  return (
    <div>
      <b>{r.title}</b>
      <div className="font-mono text-xs text-muted-foreground">{r.public_id}</div>
    </div>
  );
}

function ItemForm({ kind, city, item, next, onDone, onCancel }: { kind: HomeKind; city: Place | null; item: HomeItem | null; next: number; onDone: (message: string) => void; onCancel: () => void }) {
  const [position, setPosition] = useState(String(item?.position ?? Math.min(next, 50)));
  const [active, setActive] = useState(item?.active ?? true);
  const [startsOn, setStartsOn] = useState(item?.starts_on ?? "");
  const [endsOn, setEndsOn] = useState(item?.ends_on ?? "");
  const [headline, setHeadline] = useState(item?.headline ?? "");
  const [text, setText] = useState(item?.text ?? "");
  const [scene, setScene] = useState(item?.scene ?? "house");
  const [imageKey, setImageKey] = useState<string | null>(item?.image_key ?? null);
  const [preview, setPreview] = useState<string | null>(item?.image ?? null);
  const [alt, setAlt] = useState(item?.alt ?? "");
  const [link, setLink] = useState(item?.link ?? "");
  const [label, setLabel] = useState(item?.label ?? "");
  const [publicId, setPublicId] = useState(item?.public_id ?? "");
  const [locality, setLocality] = useState<{ id: string; name: string } | null>(item?.location_id ? { id: item.location_id, name: item.location ?? item.location_id } : null);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState<Place[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (kind !== "locality" || q.trim().length < 2) return setOptions([]);
    const t = setTimeout(() => {
      const params = new URLSearchParams({ q, kind: "locality", status: "active", limit: "8", ...(city ? { city_id: city.id } : {}) });
      api<Place[]>(`/admin/locations?${params}`)
        .then(setOptions)
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, kind, city]);

  async function upload(file: File) {
    setError("");
    setUploading(true);
    try {
      const { key, upload_url } = await api<{ key: string; upload_url: string }>("/admin/home/image", {
        method: "POST",
        body: { content_type: file.type, size_bytes: file.size },
      });
      const put = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!put.ok) throw new Error("The photo did not upload; try again");
      setImageKey(key);
      setPreview(URL.createObjectURL(file));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    const body = {
      kind,
      city_id: city?.id ?? null,
      position: Number(position) || 1,
      active,
      starts_on: startsOn || null,
      ends_on: endsOn || null,
      ...(kind === "slide" && { headline, text, scene, image_key: imageKey, alt: alt || null, link: link || null }),
      ...(kind === "try" && { label, link }),
      ...((kind === "pick" || kind === "project") && { public_id: publicId.trim() }),
      ...(kind === "locality" && { location_id: locality?.id ?? null }),
    };
    if (kind === "locality" && !locality) return setError("Choose the locality");
    try {
      await api(item ? `/admin/home/${item.id}` : "/admin/home", { method: item ? "PUT" : "POST", body });
      onDone(item ? "Saved." : "Added to the home page.");
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Card>
      <form className="flex flex-col gap-3" onSubmit={save} aria-label={item ? "Edit home page item" : "Add home page item"}>
        <CardTitle>
          {item ? "Edit" : "Add"} · {city ? city.name : "everywhere"}
        </CardTitle>

        {kind === "slide" && (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <Label>
                Headline
                <Input value={headline} maxLength={80} onChange={(e) => setHeadline(e.target.value)} required />
              </Label>
              <Label>
                Line under it
                <Input value={text} maxLength={160} onChange={(e) => setText(e.target.value)} required />
              </Label>
              <Label>
                Drawn scene (used when there is no photo)
                <NativeSelect value={scene} onChange={(e) => setScene(e.target.value)}>
                  {SCENES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </NativeSelect>
              </Label>
              <Label>
                “See more” link (optional, a RealRoof page)
                <Input value={link} maxLength={300} placeholder="/search?purpose=sell&project=true" onChange={(e) => setLink(e.target.value)} />
              </Label>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <Label>
                Photo (optional; JPG / PNG / WebP up to 5 MB, wide, about 2400 × 1000; only photos we own)
                <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              </Label>
              {uploading && <span className="text-sm text-muted-foreground">Uploading…</span>}
              {preview && <img src={preview} alt="" className="h-20 w-48 rounded object-cover" />}
              {imageKey && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setImageKey(null);
                    setPreview(null);
                  }}
                >
                  Use the drawing instead
                </Button>
              )}
            </div>
            {imageKey && (
              <Label>
                Photo description (alt text)
                <Input value={alt} maxLength={160} onChange={(e) => setAlt(e.target.value)} required />
              </Label>
            )}
          </>
        )}

        {kind === "try" && (
          <div className="grid gap-3 md:grid-cols-2">
            <Label>
              Chip text
              <Input value={label} maxLength={60} placeholder="Hall for 300 guests, Kakinada" onChange={(e) => setLabel(e.target.value)} required />
            </Label>
            <Label>
              Opens (a RealRoof page; run the search on the site and copy from /search)
              <Input value={link} maxLength={300} placeholder="/search?module=hall&guests=300" onChange={(e) => setLink(e.target.value)} required />
            </Label>
          </div>
        )}

        {(kind === "pick" || kind === "project") && (
          <Label>
            {kind === "project" ? "Project ad number" : "Ad number"}
            <Input value={publicId} maxLength={20} placeholder="RR-S-000412" onChange={(e) => setPublicId(e.target.value)} required />
          </Label>
        )}

        {kind === "locality" &&
          (locality ? (
            <div className="flex items-center gap-2">
              Locality: <strong>{locality.name}</strong>
              <Button variant="link" onClick={() => setLocality(null)}>
                Change
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Label>
                Locality{city ? ` in ${city.name}` : ""}
                <Input placeholder="Type a locality" value={q} onChange={(e) => setQ(e.target.value)} />
              </Label>
              {options.length > 0 && (
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Locality suggestions">
                  {options.map((p) => (
                    <Button key={p.id} size="sm" onClick={() => (setLocality({ id: p.id, name: p.name }), setQ(""))}>
                      {p.name}
                      {p.city ? `, ${p.city}` : ""}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ))}

        <div className="grid gap-3 md:grid-cols-3">
          <Label>
            Order (1 first)
            <Input type="number" min={1} max={50} value={position} onChange={(e) => setPosition(e.target.value)} />
          </Label>
          <Label>
            From (optional)
            <Input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          </Label>
          <Label>
            Until (optional)
            <Input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          </Label>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Show on the home page
        </label>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={uploading}>
            Save
          </Button>
          <Button type="button" variant="link" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
